/**
 * API service for blog operations
 * Handles all HTTP requests to the blog endpoints
 */

import APIService from "./APIService";

const BlogsAPI = {
  /**
   * Get recent blog posts with a specified limit
   * @param {number} limit - Number of recent blogs to retrieve
   * @returns {Promise<Array>} Array of blog post objects
   */
  async getRecentBlogs(limit = 10) {
    try {
      console.log(`🔄 Fetching ${limit} recent blogs from API...`);

      const blogs = await APIService.get(`/api/v1/app/blogs?limit=${limit}`);

      if (blogs) {
        console.log(`✅ Successfully retrieved ${blogs.length} blogs from API`);
        return blogs;
      }

      return [];
    } catch (error) {
      console.error("❌ Error fetching recent blogs:", error);
      throw new Error(`Failed to fetch recent blogs: ${error.message}`);
    }
  },

  /**
   * Get a specific blog post by short title
   * @param {string} shortTitle - Short title of the blog post to retrieve
   * @returns {Promise<Object>} Blog post object with metadata and HTML content
   */
  async getBlogByShortTitle(shortTitle) {
    try {
      console.log(
        `🔄 Fetching blog with short title: ${shortTitle} from API...`
      );

      const blog = await APIService.get(
        `/api/v1/app/blogs/by-short-title/${encodeURIComponent(shortTitle)}`
      );

      if (blog) {
        console.log(`✅ Successfully retrieved blog: "${blog.title}"`);
        return blog;
      }

      throw new Error("Blog post not found");
    } catch (error) {
      console.error(
        `❌ Error fetching blog with short title ${shortTitle}:`,
        error
      );
      throw new Error(`Failed to fetch blog: ${error.message}`);
    }
  },

  /**
   * Get paginated blog posts
   * @param {number} page - Page number (0-based)
   * @param {number} size - Page size
   * @returns {Promise<Object>} Paginated blog response
   */
  async getBlogsPaginated(page = 0, size = 20) {
    try {
      console.log(`🔄 Fetching paginated blogs: page ${page}, size ${size}...`);

      const paginatedBlogs = await APIService.get(
        `/api/v1/app/blogs/paginated?page=${page}&size=${size}`
      );

      if (paginatedBlogs) {
        console.log(
          `✅ Successfully retrieved paginated blogs: ${paginatedBlogs.totalElements} total`
        );
        return paginatedBlogs;
      }

      return { content: [], totalElements: 0, totalPages: 0 };
    } catch (error) {
      console.error("❌ Error fetching paginated blogs:", error);
      throw new Error(`Failed to fetch paginated blogs: ${error.message}`);
    }
  },

  /**
   * Get blog posts by category
   * @param {string} category - Blog category
   * @param {number} page - Page number (0-based)
   * @param {number} size - Page size
   * @returns {Promise<Object>} Paginated blog response filtered by category
   */
  async getBlogsByCategory(category, page = 0, size = 20) {
    try {
      console.log(
        `🔄 Fetching blogs by category: ${category}, page ${page}, size ${size}...`
      );

      const categoryBlogs = await APIService.get(
        `/api/v1/app/blogs/category/${encodeURIComponent(
          category
        )}?page=${page}&size=${size}`
      );

      if (categoryBlogs) {
        console.log(
          `✅ Successfully retrieved ${categoryBlogs.totalElements} blogs for category: ${category}`
        );
        return categoryBlogs;
      }

      return { content: [], totalElements: 0, totalPages: 0 };
    } catch (error) {
      console.error(`❌ Error fetching blogs by category ${category}:`, error);
      throw new Error(`Failed to fetch blogs by category: ${error.message}`);
    }
  },

  /**
   * Generate a new blog post from URL
   * @param {Object} requestData - Blog generation request data
   * @param {string} requestData.url - URL to generate blog from
   * @param {string} requestData.category - Optional category
   * @param {string} requestData.author - Optional author
   * @returns {Promise<Object>} Blog generation response (202 status response)
   */
  async generateBlogFromUrl(requestData) {
    try {
      console.log(`🔄 Generating blog from URL: ${requestData.url}...`);

      const response = await APIService.post(
        `/api/v1/app/blogs/generate`,
        requestData
      );

      if (response) {
        // Check if this is a 202 processing response or completed blog
        if (response.status === "processing") {
          console.log(`✅ Blog generation started: ${response.message}`);
          return response;
        } else if (response.status === "completed" && response.blog) {
          console.log(`✅ Blog already exists: "${response.blog.title}"`);
          return response;
        } else if (response.title) {
          // Handle direct blog response (legacy/fallback)
          console.log(`✅ Successfully generated blog: "${response.title}"`);
          return response;
        }
      }

      throw new Error("Failed to generate blog post");
    } catch (error) {
      console.error(
        `❌ Error generating blog from URL ${requestData.url}:`,
        error
      );

      // Since errors are now handled globally in APIService,
      // we can just re-throw any error here
      throw error;
    }
  },

  /**
   * Generate a blog post from multiple URLs
   * @param {Object} requestData - Request data for blog generation
   * @param {Array<string>} requestData.urls - Array of URLs to generate blog from
   * @param {number} requestData.wordCount - Target word count for the blog
   * @param {string} requestData.category - Optional category
   * @param {string} requestData.author - Optional author
   * @returns {Promise<Object>} Blog generation response (202 status response)
   */
  async generateBlogFromUrls(requestData) {
    try {
      console.log(`🔄 Generating blog from ${requestData.urls.length} URLs...`);

      const response = await APIService.post(
        `/api/v1/app/blogs/generate-from-urls`,
        requestData
      );

      if (response) {
        // Check if this is a 202 processing response or completed blog
        if (response.status === "processing") {
          console.log(`✅ Blog generation started: ${response.message}`);
          return response;
        } else if (response.status === "completed" && response.blog) {
          console.log(`✅ Blog already exists: "${response.blog.title}"`);
          return response;
        } else if (response.title) {
          // Handle direct blog response (legacy/fallback)
          console.log(`✅ Successfully generated blog: "${response.title}"`);
          return response;
        }
      }

      throw new Error("Failed to generate blog post from URLs");
    } catch (error) {
      console.error(`❌ Error generating blog from URLs:`, error);

      // Since errors are now handled globally in APIService,
      // we can just re-throw any error here
      throw error;
    }
  },

  /**
   * Check blog generation status for a URL
   * @param {string} url - URL to check status for
   * @returns {Promise<Object>} Status response with state and optional blog data
   */
  async checkBlogStatus(url) {
    try {
      console.log(`🔄 Checking blog generation status for URL: ${url}...`);

      const status = await APIService.get(
        `/api/v1/app/blogs/status?url=${encodeURIComponent(url)}`
      );

      if (status) {
        console.log(`✅ Blog status for ${url}: ${status.status}`);
        return status;
      }

      throw new Error("Failed to get blog status");
    } catch (error) {
      console.error(`❌ Error checking blog status for ${url}:`, error);
      throw new Error(`Failed to check blog status: ${error.message}`);
    }
  },

  /**
   * Search blogs by keyword with pagination
   * @param {string} searchTerm - The search term
   * @param {number} page - Page number (0-based)
   * @param {number} size - Number of items per page
   * @returns {Promise<Object>} Paginated search results
   */
  async searchBlogs(searchTerm, page = 0, size = 10) {
    try {
      console.log(
        `🔍 Searching blogs for: "${searchTerm}" (page ${page}, size ${size})`
      );

      const params = new URLSearchParams({
        q: searchTerm,
        page: page.toString(),
        size: size.toString(),
      });

      const response = await APIService.get(
        `/api/v1/app/blogs/search?${params}`
      );

      if (response) {
        console.log(
          `✅ Found ${response.totalElements} blogs matching "${searchTerm}"`
        );
        return response;
      }

      return { content: [], totalPages: 0, totalElements: 0, number: page };
    } catch (error) {
      console.error(`❌ Error searching blogs for "${searchTerm}":`, error);
      return { content: [], totalPages: 0, totalElements: 0, number: page };
    }
  },

  /**
   * Search blogs by keyword within a specific category
   * @param {string} searchTerm - The search term
   * @param {string} category - The category to search within
   * @param {number} page - Page number (0-based)
   * @param {number} size - Number of items per page
   * @returns {Promise<Object>} Paginated search results
   */
  async searchBlogsByCategory(searchTerm, category, page = 0, size = 10) {
    try {
      console.log(`🔍 Searching "${category}" blogs for: "${searchTerm}"`);

      const params = new URLSearchParams({
        q: searchTerm,
        category: category,
        page: page.toString(),
        size: size.toString(),
      });

      const response = await APIService.get(
        `/api/v1/app/blogs/search/category?${params}`
      );

      if (response) {
        console.log(
          `✅ Found ${response.totalElements} "${category}" blogs matching "${searchTerm}"`
        );
        return response;
      }

      return { content: [], totalPages: 0, totalElements: 0, number: page };
    } catch (error) {
      console.error(
        `❌ Error searching ${category} blogs for "${searchTerm}":`,
        error
      );
      return { content: [], totalPages: 0, totalElements: 0, number: page };
    }
  },
};

// Export a singleton instance
export default BlogsAPI;
